/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ViewCourseIdModal
 *
 * @file ViewCourseIdModal.jsx
 * @author Ujjwal Sana
 * @since 1.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  IconButton
} from '@material-ui/core';
import Icons from '../icons/Icon';
import useCourseIdModalStyles from './styles/viewCourseIdModalStyle';

const ViewCourseIdModal = ({ open, onClose, courseId, textInfo }) => {
  const classes = useCourseIdModalStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator?.clipboard?.writeText(courseId);
    setCopied(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Close */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        className={classes.closeButton}
      >
        <Icons name="CloseIcon" />
      </IconButton>

      {/* Content */}
      <DialogContent classes={{ root: classes.contentRoot }}>
        <div className={classes.sectionCard}>
          <div className={classes.mainRow}>
            {/* Left side */}
            <div className={classes.courseIdGroup}>
              <span className={classes.courseIdLabel}>{textInfo.courseLabel}</span>
              <span className={classes.courseIdText} title={courseId}>
                {courseId}
              </span>
            </div>

            {/* Right side – IconButton */}
            <IconButton
              className={`${classes.copyIconButton} ${
                copied ? classes.copied : ''
              }`}
              onClick={handleCopy}
            >
              <div className={classes.iconTextWrapper}>
                {copied ? (
                  <Icons name="CopiedIcon" />
                ) : (
                  <Icons name="CopyIcon" />
                )}
                <span className={classes.copyText}>
                  {copied ? textInfo.copied : textInfo.copy}
                </span>
              </div>
            </IconButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ViewCourseIdModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  courseId: PropTypes.string.isRequired,
  textInfo: PropTypes.shape({
    copy: PropTypes.string,
    copied: PropTypes.string,
    courseLabel: PropTypes.string
  })
};

ViewCourseIdModal.defaultProps = {
  open: false,
  onClose: () => {},
  textInfo: { copy: 'Copy', copied: 'Copied', courseLabel: 'Course ID:' }
};

export default ViewCourseIdModal;
